import { MouseEventHandler } from 'react'

import clsx from 'clsx'

import { ChildProps } from '@/common/util/props'

import AiLoadingIcon from '@/components/view/atoms/icon/AiLoadingIcon'
import Icon, { IconType } from '@/components/view/atoms/icon/Icon'
import AiDots from '@/components/view/atoms/loading/AiDots'
import InternalLink from '@/components/view/utils/link/InternalLink'

const AiShine = () => (
  <span className='animate-shine absolute top-1/2 -left-1/4 size-[200%] -translate-x-1/2 -translate-y-1/2 -rotate-45 bg-linear-to-b from-transparent via-white/60 to-transparent motion-reduce:animate-none' />
)

interface Props extends ChildProps {
  type?: 'submit' | 'reset' | 'button'
  size?: 'base' | 'large' | 'full'
  link?: string
  disabled?: boolean
  onClick?: MouseEventHandler<HTMLButtonElement>
  nowrap?: boolean
  /** AI処理中。アイコンをアニメーションに差し替え、操作を受け付けない */
  loading?: boolean
}

const AiButton = ({
  children,
  type = 'button',
  size = 'base',
  link,
  disabled = false,
  onClick,
  nowrap = false,
  loading = false
}: Props) => {
  const buttonSize =
    size == 'base'
      ? 'text-size-12 h-6.5 w-fit'
      : size == 'large'
        ? 'text-size-13 h-8 w-fit'
        : 'text-size-14 py-2 justify-center w-full'

  const className =
    'inline-flex cursor-pointer rounded-lg leading-100 disabled:pointer-events-none' +
    (loading ? ' relative overflow-hidden' : '') +
    (nowrap ? ' shrink-0' : '')
  // ローディング時のドット（高さ19px）が base の高さ(26px)を超えるため、縦の余白を詰める
  const textPaddingClass = loading && size == 'base' ? 'py-0' : 'py-1.5'
  const textClassName = clsx(
    'block text-ai font-bold z-10 flex items-center justify-center gap-1 px-3',
    textPaddingClass
  )
  const colorClassName = loading
    ? 'bg-fill-ai-secondary-active shadow-button-inset pointer-events-none'
    : disabled
      ? 'bg-fill-disabled '
      : 'border-ai-button'

  const label = (
    <div className={textClassName}>
      {loading ? <AiLoadingIcon size={16} /> : <Icon type={IconType.AI_FILL} size={16} />}
      {loading ? (
        <span className='flex items-center'>
          {children}
          <AiDots />
        </span>
      ) : (
        children
      )}
    </div>
  )
  return (
    <>
      {link ? (
        <InternalLink
          link={link}
          className={clsx(className, buttonSize, colorClassName)}
          tabIndex={disabled || loading ? -1 : 0}
        >
          {label}
          {loading && <AiShine />}
        </InternalLink>
      ) : (
        <button
          type={type}
          className={clsx(className, buttonSize, colorClassName)}
          onClick={onClick}
          disabled={disabled || loading}
          aria-busy={loading}
        >
          {label}
          {loading && <AiShine />}
        </button>
      )}
    </>
  )
}

export default AiButton
